import React, { useState, useEffect } from 'react';
import {
  Inspection,
  InspectionPlan,
  InspectionDashboardStats,
  InspectionStatus,
  InspectionType,
} from './types';
import { inspectionService } from './inspectionService';
import { InspectionListTable } from './components/InspectionListTable';
import { InspectionPlanTab } from './components/InspectionPlanTab';
import { InspectionDetailModal } from './components/InspectionDetailModal';
import { CreateInspectionModal } from './components/CreateInspectionModal';
import { CreateInspectionPlanModal } from './components/CreateInspectionPlanModal';

export const InspectionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'INSPECTIONS' | 'PLANS'>('INSPECTIONS');
  const [filterStatus, setFilterStatus] = useState<'ALL' | InspectionStatus>('ALL');
  const [filterType, setFilterType] = useState<'ALL' | InspectionType>('ALL');

  const [stats, setStats] = useState<InspectionDashboardStats | null>(null);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [plans, setPlans] = useState<InspectionPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [showCreateInspection, setShowCreateInspection] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [statsData, inspData, plansData] = await Promise.all([
        inspectionService.getDashboardStats(),
        inspectionService.getInspections(),
        inspectionService.getPlans(),
      ]);
      setStats(statsData);
      setInspections(inspData);
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading quality inspections data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY MANAGEMENT</div>
        <h1>Quality Inspections & Physical Asset Controls</h1>
        <p>Aircraft walkaround inspections, ramp/turnaround checks, cabin safety, calibrated tool oversight, and dynamic checklist verification.</p>
      </div>

      {/* KPI Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Inspections</div>
            <div className="stat-value">{stats.totalInspections}</div>
            <div className="stat-sub">Registered checks</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">In Progress</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats.inProgressCount}</div>
            <div className="stat-sub">Active fieldwork</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Scheduled</div>
            <div className="stat-value" style={{ color: '#475569' }}>{stats.scheduledCount}</div>
            <div className="stat-sub">Upcoming checks</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Completed</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{stats.completedCount}</div>
            <div className="stat-sub">Pass rate: {stats.averagePassRate}%</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Stop-Work / AOG</div>
            <div className="stat-value" style={{ color: '#ef4444' }}>{stats.criticalContainmentsCount}</div>
            <div className="stat-sub">Critical groundings</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open Findings</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats.openFindings}</div>
            <div className="stat-sub">Total findings: {stats.totalFindings}</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Average Pass Rate</div>
            <div className="stat-value" style={{ color: '#4f46e5' }}>%{stats.averagePassRate}</div>
            <div className="stat-sub">Checklist verification</div>
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('INSPECTIONS')}
          className={`tab-btn ${activeTab === 'INSPECTIONS' ? 'active' : ''}`}
        >
          Inspections ({inspections.length})
        </button>
        <button
          onClick={() => setActiveTab('PLANS')}
          className={`tab-btn ${activeTab === 'PLANS' ? 'active' : ''}`}
        >
          Inspection Plans ({plans.length})
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === 'INSPECTIONS' && (
        <InspectionListTable
          inspections={inspections}
          loading={loading}
          filterStatus={filterStatus}
          filterType={filterType}
          onFilterStatusChange={setFilterStatus}
          onFilterTypeChange={setFilterType}
          onSelectInspection={(item) => setSelectedInspection(item)}
          onOpenCreate={() => setShowCreateInspection(true)}
        />
      )}

      {activeTab === 'PLANS' && (
        <InspectionPlanTab
          plans={plans}
          loading={loading}
          onOpenCreate={() => setShowCreatePlan(true)}
        />
      )}

      {/* Modals */}
      {selectedInspection && (
        <InspectionDetailModal
          inspection={selectedInspection}
          onClose={() => setSelectedInspection(null)}
          onRefresh={loadAllData}
        />
      )}

      {showCreateInspection && (
        <CreateInspectionModal
          onClose={() => setShowCreateInspection(false)}
          onSuccess={() => {
            setShowCreateInspection(false);
            loadAllData();
          }}
        />
      )}

      {showCreatePlan && (
        <CreateInspectionPlanModal
          onClose={() => setShowCreatePlan(false)}
          onSuccess={() => {
            setShowCreatePlan(false);
            loadAllData();
          }}
        />
      )}
    </div>
  );
};

export default InspectionsPage;
